import { useState, useEffect, useCallback } from "react";
import { Button } from "./ui/button";
import { toast } from "sonner";
import {
  Play,
  X,
  Plus,
  Trash2,
  Share2,
  Copy,
  Check,
  Film,
  Video,
  Archive,
  Crosshair,
  Loader2,
  ExternalLink,
  Link2,
} from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CATEGORIES = [
  { key: "featured", label: "Featured", icon: Play },
  { key: "game_film", label: "Game Film", icon: Film },
  { key: "skills_drills", label: "Skills/Drills", icon: Crosshair },
  { key: "archives", label: "Archives", icon: Archive },
];

// Extract YouTube/Vimeo video ID and type
function parseVideoUrl(url) {
  if (!url) return null;
  // YouTube
  const ytMatch = url.match(
    /(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/|youtube\.com\/shorts\/)([a-zA-Z0-9_-]{11})/
  );
  if (ytMatch) return { type: "youtube", id: ytMatch[1] };
  // Vimeo
  const vimeoMatch = url.match(/vimeo\.com\/(\d+)/);
  if (vimeoMatch) return { type: "vimeo", id: vimeoMatch[1] };
  // Direct MP4/video
  if (/\.(mp4|mov|webm|avi|mkv)(\?|$)/i.test(url)) return { type: "direct", id: url };
  // Fallback - treat as direct link
  return { type: "link", id: url };
}

function getThumbnail(url, thumbnailUrl) {
  if (thumbnailUrl) return thumbnailUrl;
  const parsed = parseVideoUrl(url);
  if (!parsed) return null;
  if (parsed.type === "youtube") return `https://img.youtube.com/vi/${parsed.id}/hqdefault.jpg`;
  if (parsed.type === "vimeo") return `https://vumbnail.com/${parsed.id}.jpg`;
  return null;
}

// Lightbox Modal for video playback
function VideoLightbox({ video, onClose }) {
  const parsed = parseVideoUrl(video.url);

  useEffect(() => {
    const handleEsc = (e) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", handleEsc);
    return () => document.removeEventListener("keydown", handleEsc);
  }, [onClose]);

  const renderPlayer = () => {
    if (!parsed) return <p className="text-white">Unable to play this video</p>;
    if (parsed.type === "youtube") {
      return (
        <iframe
          src={`https://www.youtube.com/embed/${parsed.id}?autoplay=1&rel=0`}
          className="w-full aspect-video rounded-lg"
          allow="autoplay; encrypted-media; picture-in-picture"
          allowFullScreen
          title={video.title}
        />
      );
    }
    if (parsed.type === "vimeo") {
      return (
        <iframe
          src={`https://player.vimeo.com/video/${parsed.id}?autoplay=1`}
          className="w-full aspect-video rounded-lg"
          allow="autoplay; fullscreen"
          allowFullScreen
          title={video.title}
        />
      );
    }
    if (parsed.type === "direct") {
      return (
        <video controls autoPlay className="w-full aspect-video rounded-lg bg-black" src={video.url}>
          Your browser does not support the video tag.
        </video>
      );
    }
    // Fallback: link
    return (
      <div className="text-center py-12">
        <p className="text-white mb-4">This video can't be embedded directly.</p>
        <a href={video.url} target="_blank" rel="noopener noreferrer" className="text-blue-400 underline flex items-center justify-center gap-2">
          Open in new tab <ExternalLink size={16} />
        </a>
      </div>
    );
  };

  return (
    <div
      className="fixed inset-0 bg-black/90 z-[200] flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="video-lightbox"
    >
      <div
        className="w-full max-w-4xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-white font-semibold text-lg truncate pr-4">{video.title}</h3>
          <button
            onClick={onClose}
            className="text-white/70 hover:text-white p-1 rounded-full hover:bg-white/10 transition"
            data-testid="lightbox-close"
          >
            <X size={24} />
          </button>
        </div>
        {renderPlayer()}
      </div>
    </div>
  );
}

// Video thumbnail card
function VideoCard({ video, onClick, onShare, onDelete, canDelete }) {
  const thumb = getThumbnail(video.url, video.thumbnail_url);
  const [copied, setCopied] = useState(false);

  const handleShare = (e) => {
    e.stopPropagation();
    navigator.clipboard.writeText(video.url).then(() => {
      setCopied(true);
      toast.success("Video link copied!");
      setTimeout(() => setCopied(false), 2000);
    });
  };

  return (
    <div
      className="group relative rounded-xl overflow-hidden bg-gray-900 cursor-pointer border border-gray-200 hover:border-blue-400 hover:shadow-lg transition-all"
      onClick={() => onClick(video)}
      data-testid={`video-card-${video.video_id}`}
    >
      {/* Thumbnail with 16:9 ratio */}
      <div className="relative aspect-video bg-gray-800">
        {thumb ? (
          <img
            src={thumb}
            alt={video.title}
            className="w-full h-full object-cover"
            loading="lazy"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-gray-700 to-gray-900">
            <Video size={40} className="text-gray-500" />
          </div>
        )}
        {/* Play button overlay */}
        <div className="absolute inset-0 bg-black/30 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
          <div className="w-14 h-14 rounded-full bg-white/90 flex items-center justify-center shadow-xl">
            <Play size={28} className="text-gray-900 ml-1" />
          </div>
        </div>
        {/* Category badge */}
        <div className="absolute top-2 left-2">
          <span className="px-2 py-0.5 rounded text-[10px] font-semibold uppercase tracking-wider bg-black/60 text-white backdrop-blur-sm">
            {video.category === "game_film" ? "Game Film" : video.category === "skills_drills" ? "Skills/Drills" : video.category === "archives" ? "Archives" : "Featured"}
          </span>
        </div>
      </div>
      {/* Caption */}
      <div className="p-3 bg-white">
        <p className="text-sm font-medium text-gray-900 truncate" data-testid={`video-title-${video.video_id}`}>{video.title}</p>
        <div className="flex items-center justify-between mt-2">
          <span className="text-xs text-gray-400">
            {video.created_at ? new Date(video.created_at).toLocaleDateString() : ""}
          </span>
          <div className="flex items-center gap-1">
            <button
              onClick={handleShare}
              className="p-1.5 rounded-md hover:bg-gray-100 text-gray-400 hover:text-blue-600 transition"
              title="Copy video link"
              data-testid={`share-video-${video.video_id}`}
            >
              {copied ? <Check size={14} className="text-green-500" /> : <Link2 size={14} />}
            </button>
            {canDelete && (
              <button
                onClick={(e) => { e.stopPropagation(); onDelete(video.video_id); }}
                className="p-1.5 rounded-md hover:bg-red-50 text-gray-400 hover:text-red-500 transition"
                title="Delete video"
                data-testid={`delete-video-${video.video_id}`}
              >
                <Trash2 size={14} />
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// Add Video Form
function AddVideoForm({ onSubmit, onCancel, defaultCategory }) {
  const [title, setTitle] = useState("");
  const [url, setUrl] = useState("");
  const [category, setCategory] = useState(defaultCategory || "featured");
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!title.trim() || !url.trim()) {
      toast.error("Title and URL are required");
      return;
    }
    setSubmitting(true);
    try {
      await onSubmit({ title: title.trim(), url: url.trim(), category });
      setTitle("");
      setUrl("");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="bg-gray-50 rounded-xl p-4 border border-gray-200 space-y-3" data-testid="add-video-form">
      <h4 className="font-semibold text-gray-900 text-sm">Add Video</h4>
      <input
        type="text"
        placeholder="Video title (e.g. 2025 Season Highlights)"
        value={title}
        onChange={(e) => setTitle(e.target.value)}
        className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
        data-testid="video-title-input"
      />
      <input
        type="url"
        placeholder="Video URL (YouTube, Vimeo, or MP4)"
        value={url}
        onChange={(e) => setUrl(e.target.value)}
        className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
        data-testid="video-url-input"
      />
      <select
        value={category}
        onChange={(e) => setCategory(e.target.value)}
        className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none bg-white"
        data-testid="video-category-select"
      >
        {CATEGORIES.map((c) => (
          <option key={c.key} value={c.key}>{c.label}</option>
        ))}
      </select>
      <div className="flex gap-2 pt-1">
        <Button
          type="submit"
          disabled={submitting}
          className="bg-blue-900 hover:bg-blue-800 text-white text-sm"
          data-testid="submit-video-btn"
        >
          {submitting ? <Loader2 size={14} className="animate-spin mr-1" /> : <Plus size={14} className="mr-1" />}
          Add Video
        </Button>
        <Button type="button" variant="ghost" size="sm" onClick={onCancel} data-testid="cancel-video-btn">
          Cancel
        </Button>
      </div>
    </form>
  );
}

// Main VideoShowcase component
export function VideoShowcase({ athleteId, canEdit = false, isAdmin = false }) {
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("featured");
  const [lightboxVideo, setLightboxVideo] = useState(null);
  const [showAddForm, setShowAddForm] = useState(false);

  const fetchVideos = useCallback(async () => {
    if (!athleteId) return;
    try {
      const res = await fetch(`${API_URL}/api/player/${athleteId}/videos`);
      if (res.ok) {
        const data = await res.json();
        setVideos(data.videos || []);
      }
    } catch {
      // Silently fail - showcase is optional
    } finally {
      setLoading(false);
    }
  }, [athleteId]);

  useEffect(() => { fetchVideos(); }, [fetchVideos]);

  const handleAddVideo = async (videoData) => {
    const token = localStorage.getItem("token");
    const endpoint = isAdmin
      ? `${API_URL}/api/admin/player/${athleteId}/videos`
      : `${API_URL}/api/athlete/videos`;

    const res = await fetch(endpoint, {
      method: "POST",
      headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
      body: JSON.stringify(videoData),
    });

    if (res.ok) {
      toast.success("Video added to showcase!");
      setShowAddForm(false);
      fetchVideos();
    } else {
      const err = await res.json().catch(() => ({}));
      toast.error(err.detail || "Failed to add video");
    }
  };

  const handleDeleteVideo = async (videoId) => {
    if (!window.confirm("Remove this video from your showcase?")) return;
    const token = localStorage.getItem("token");
    const endpoint = isAdmin
      ? `${API_URL}/api/admin/player/${athleteId}/videos/${videoId}`
      : `${API_URL}/api/athlete/videos/${videoId}`;

    const res = await fetch(endpoint, {
      method: "DELETE",
      headers: { Authorization: `Bearer ${token}` },
    });
    if (res.ok) {
      toast.success("Video removed");
      fetchVideos();
    } else {
      toast.error("Failed to remove video");
    }
  };

  const featuredVideos = videos.filter((v) => v.category === "featured");
  const tabVideos = activeTab === "featured"
    ? featuredVideos
    : videos.filter((v) => v.category === activeTab);

  if (loading) {
    return (
      <div className="bg-white rounded-xl shadow-sm p-6" data-testid="video-showcase-loading">
        <div className="flex items-center gap-3 mb-4">
          <Video size={20} className="text-blue-900" />
          <h3 className="text-lg font-bold text-gray-900">Video Showcase</h3>
        </div>
        <div className="flex items-center justify-center py-8">
          <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="video-showcase">
      {/* Featured Four Section */}
      {featuredVideos.length > 0 && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6" data-testid="featured-videos-section">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <Play size={18} className="text-red-600" />
              <h3 className="text-lg font-bold text-gray-900">Featured Videos</h3>
              <span className="text-xs text-gray-400 ml-1">Scout's First Look</span>
            </div>
            {canEdit && featuredVideos.length < 4 && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => { setActiveTab("featured"); setShowAddForm(true); }}
                className="text-xs"
                data-testid="add-featured-video-btn"
              >
                <Plus size={14} className="mr-1" /> Add
              </Button>
            )}
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {featuredVideos.slice(0, 4).map((video) => (
              <VideoCard
                key={video.video_id}
                video={video}
                onClick={setLightboxVideo}
                canDelete={canEdit}
                onDelete={handleDeleteVideo}
              />
            ))}
          </div>
        </div>
      )}

      {/* Extended Video Library with Tabs */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6" data-testid="video-library-section">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <Film size={18} className="text-blue-900" />
            <h3 className="text-lg font-bold text-gray-900">Video Library</h3>
          </div>
          {canEdit && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowAddForm(!showAddForm)}
              className="text-xs"
              data-testid="add-video-library-btn"
            >
              <Plus size={14} className="mr-1" /> {showAddForm ? "Close" : "Add Video"}
            </Button>
          )}
        </div>

        {/* Add Video Form */}
        {showAddForm && canEdit && (
          <div className="mb-4">
            <AddVideoForm
              onSubmit={handleAddVideo}
              onCancel={() => setShowAddForm(false)}
              defaultCategory={activeTab}
            />
          </div>
        )}

        {/* Tabs */}
        <div className="flex gap-1 border-b border-gray-200 mb-4 overflow-x-auto" data-testid="video-tabs">
          {CATEGORIES.map((cat) => {
            const Icon = cat.icon;
            const count = videos.filter((v) => v.category === cat.key).length;
            return (
              <button
                key={cat.key}
                onClick={() => setActiveTab(cat.key)}
                className={`flex items-center gap-1.5 px-4 py-2.5 text-sm font-medium whitespace-nowrap border-b-2 transition-colors ${
                  activeTab === cat.key
                    ? "border-blue-900 text-blue-900"
                    : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                }`}
                data-testid={`video-tab-${cat.key}`}
              >
                <Icon size={14} />
                {cat.label}
                {count > 0 && (
                  <span className={`ml-1 px-1.5 py-0.5 rounded-full text-[10px] font-semibold ${
                    activeTab === cat.key ? "bg-blue-900 text-white" : "bg-gray-100 text-gray-500"
                  }`}>
                    {count}
                  </span>
                )}
              </button>
            );
          })}
        </div>

        {/* Tab Content */}
        {tabVideos.length > 0 ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {tabVideos.map((video) => (
              <VideoCard
                key={video.video_id}
                video={video}
                onClick={setLightboxVideo}
                canDelete={canEdit}
                onDelete={handleDeleteVideo}
              />
            ))}
          </div>
        ) : (
          <div className="text-center py-10 text-gray-400" data-testid="no-videos-message">
            <Video size={40} className="mx-auto mb-3 opacity-50" />
            <p className="text-sm">No {CATEGORIES.find((c) => c.key === activeTab)?.label.toLowerCase()} videos yet</p>
            {canEdit && (
              <Button
                variant="outline"
                size="sm"
                className="mt-3 text-xs"
                onClick={() => setShowAddForm(true)}
                data-testid="add-first-video-btn"
              >
                <Plus size={14} className="mr-1" /> Add your first video
              </Button>
            )}
          </div>
        )}
      </div>

      {/* Lightbox */}
      {lightboxVideo && (
        <VideoLightbox
          video={lightboxVideo}
          onClose={() => setLightboxVideo(null)}
        />
      )}
    </div>
  );
}

export default VideoShowcase;
